import { useEffect, useMemo, useState } from "react";

import GenericCard from "widgets/cards/generic";
import WaterRateForm from "widgets/forms/sites/rates/water";
import SewerageRateForm from "widgets/forms/sites/rates/sewerage";
import ElectricityRateForm from "widgets/forms/sites/rates/electricity";
import { ratesRateGet, ratesRateUpdate } from "api/rates";
import GenericButton from "widgets/controls/buttons/generic";
import { RateResponse, RatesUpdateBody } from "api/types/rates";
import OtherRateForm from "widgets/forms/sites/rates/other";
import GasRateForm from "widgets/forms/sites/rates/gas";
import { useTranslation } from "react-i18next";

interface GeneralProps {
  rateId?: string;
  onUpdate?: () => void;
}

const RatesSetup: React.FC<GeneralProps> = ({ rateId, onUpdate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [rates, setRates] = useState<RateResponse>(null);

  const [updatedRates, setUpdatedRates] = useState<RatesUpdateBody>(null);

  const fetchRate = async () => {
    setLoading(true);
    setRates(null);
    const { data, status, isLoading } = await ratesRateGet(rateId || "");
    setLoading(isLoading);
    if (status && status === 200) {
      // console.log(data);
      setRates({ ...data });
    }
  };

  useEffect(() => {
    if (rateId) {
      fetchRate();
    }
  }, [rateId]);

  const handleUpdate = (formData: any, type: string) => {
    setDirty(true);
    if (type === "water") {
      setUpdatedRates((prev) => ({
        ...prev,
        rateId: rateId,
        water: formData,
      }));
    } else if (type === "sewerage") {
      setUpdatedRates((prev) => ({
        ...prev,
        rateId: rateId,
        sewerage: formData,
      }));
    } else if (type === "electricity") {
      setUpdatedRates((prev) => ({
        ...prev,
        rateId: rateId,
        electricity: formData,
      }));
    } else if (type === "gas") {
      setUpdatedRates((prev) => ({
        ...prev,
        rateId: rateId,
        gas: formData,
      }));
    } else if (type === "other") {
      setUpdatedRates((prev) => ({
        ...prev,
        rateId: rateId,
        other: formData.other,
      }));
    }
  };

  const updateRate = async () => {
    const { status } = await ratesRateUpdate(updatedRates);

    if (status && status === 200) {
      setUpdatedRates(null);
      fetchRate();
      setDirty(false);
    }
  };

  const handleDiscard = () => {
    setLoading(true);
    setDirty(false);
    setUpdatedRates(null);
    const oldRates = { ...rates };

    // Set rates to null temporarily
    setRates(null);

    // Restore old rates after a short delay
    setTimeout(() => {
      setRates(oldRates);
      setLoading(false);
    }, 200); // Adjust the delay as necessary (200 ms in this example)
  };

  const rateKey = useMemo(() => `${rateId}`, [rateId]);

  return (
    <>
      <div>
        {dirty ? (
          <div className="mt-0 flex justify-end gap-2">
            <GenericButton
              text={t("button.action.save")}
              size="md"
              onClick={updateRate}
            />
            <GenericButton
              text={t("button.action.discard")}
              size="md"
              variant="outline"
              onClick={handleDiscard}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
      <div className="h-full w-full overflow-x-auto pb-2">
        <div className="flex h-full gap-4">
          <GenericCard
            title={t("site.rates.water")}
            description={t("site.rates.display")}
            bgColor="500"
            textColor="secondary"
            extra="h-full min-w-[300px] max-w-[320px] flex-shrink-0"
          >
            <div className="mt-4 flex h-full w-full flex-col gap-6 overflow-x-auto">
              {!loading && (
                <WaterRateForm
                  key={`${rateKey}-water`}
                  submit={(form) => handleUpdate(form, "water")}
                  onClose={() => {}}
                  defaultValues={rates?.water}
                />
              )}
            </div>
          </GenericCard>
          <GenericCard
            title={t("site.rates.sewerage")}
            description={t("site.rates.display")}
            bgColor="500"
            textColor="secondary"
            extra="h-full min-w-[300px] max-w-[320px] flex-shrink-0"
          >
            <div className="mt-4 flex h-full w-full flex-col gap-6 overflow-x-auto">
              {!loading && (
                <SewerageRateForm
                  key={`${rateId}-sewerage`}
                  submit={(form) => handleUpdate(form, "sewerage")}
                  onClose={() => {}}
                  defaultValues={rates?.sewerage}
                />
              )}
            </div>
          </GenericCard>
          <GenericCard
            title={t("site.rates.electricity")}
            description={t("site.rates.display")}
            bgColor="500"
            textColor="secondary"
            extra="h-full min-w-[300px] max-w-[320px] flex-shrink-0"
          >
            <div className="mt-4 flex h-full w-full flex-col gap-6 overflow-x-auto">
              {!loading && (
                <ElectricityRateForm
                  key={`${rateId}-electricity`}
                  submit={(form) => handleUpdate(form, "electricity")}
                  onClose={() => {}}
                  defaultValues={rates?.electricity}
                />
              )}
            </div>
          </GenericCard>
          <GenericCard
            title={t("site.rates.gas")}
            description={t("site.rates.display")}
            bgColor="500"
            textColor="secondary"
            extra="h-full min-w-[300px] max-w-[320px] flex-shrink-0"
          >
            <div className="mt-4 flex h-full w-full flex-col gap-6 overflow-x-auto">
              {!loading && (
                <GasRateForm
                  key={`${rateId}-gas`}
                  submit={(form) => handleUpdate(form, "gas")}
                  onClose={() => {}}
                  defaultValues={rates?.gas}
                />
              )}
            </div>
          </GenericCard>
          <GenericCard
            title={t("site.rates.other")}
            description={t("site.rates.display")}
            bgColor="500"
            textColor="secondary"
            extra="h-full min-w-[300px] max-w-[320px] flex-shrink-0"
          >
            <div className="mt-4 flex h-full w-full flex-col gap-6 overflow-x-auto">
              {!loading && (
                <OtherRateForm
                  key={`${rateId}-other`}
                  submit={(form) => handleUpdate(form, "other")}
                  onClose={() => {}}
                  defaultValues={{ other: rates?.other }}
                />
              )}
            </div>
          </GenericCard>
        </div>
      </div>
    </>
  );
};

export default RatesSetup;
